import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { get } from "lodash"
import { Creators as IntradayCreators } from "model/intraday"
import { generateIntradayBatchUrl } from "helpers/intraday"
import { useAccountAlias, useAccountId } from "resources/account"
import { DataStatus } from "helpers/data-statuses"
import { Uuid } from "helpers/types/uuid"
import { InstrumentWithMetadata } from "helpers/types/instruments/metadata"
import { getToday } from "helpers/types/dates"
import { ResourceType } from "helpers/permissions/resource-types"
import { IntradayLastBatch, IntradayQueryKey, IntradayQueryRecord, ReduxState } from "model/state"
import useResource from "./resources"

const INTRADAY_RESOURCE = "intradayLastBatch"
const EMPTY_OBJECT: Record<IntradayQueryKey, IntradayQueryRecord> = {}

export function useIntradayChannel(useIntraday: boolean, resourceId: Uuid, resourceType: ResourceType, viewName: string) {
  const intradayQueries = useSelector((state: ReduxState) => get(state, "global.intraday.queries", EMPTY_OBJECT))
  const dispatch = useDispatch()
  const currentDate = getToday()
  const accountId = useAccountId()
  const accountAlias = useAccountAlias()

  useEffect(() => {
    if (!useIntraday || !accountId || !resourceId || resourceType !== "PORTFOLIO") {
      dispatch(IntradayCreators.unregisterModuleQueries(viewName))
      return
    }

    // id is the same as the url since we're not using graphql
    const id = generateIntradayBatchUrl(accountAlias, resourceId, currentDate)
    const channel: IntradayQueryKey = `${accountId}_${resourceId}`
    const currentId = get(intradayQueries, [channel, INTRADAY_RESOURCE, "id"], null)

    if (currentId !== id) {
      if (id) {
        dispatch(IntradayCreators.registerQuery(viewName, channel, INTRADAY_RESOURCE, { url: id, id }))
      } else {
        dispatch(IntradayCreators.unregisterQuery(viewName, channel, INTRADAY_RESOURCE))
      }
    }
  }, [viewName, dispatch, intradayQueries, useIntraday, accountId, resourceId, currentDate, accountAlias, resourceType])

  // This needs to be a separate hook as we ONLY want to unregister if viewName changes
  // Unregistering otherwise will create an infinite loop.
  useEffect(() => {
    return () => {
      dispatch(IntradayCreators.unregisterModuleQueries(viewName))
    }
  }, [dispatch, viewName])
}

/**
 * Hook to get the last intraday batch data for a specific portfolio
 */
export function useLastIntradayBatch(instrument?: InstrumentWithMetadata, intradayEnabled?: boolean): [IntradayLastBatch | null, DataStatus] {
  const accountAlias = useAccountAlias()
  const currentDate = getToday()
  const { type, id } = instrument || {}

  return useResource({
    resource: "intradayLastBatch",
    url: () => type === "PORTFOLIO" && intradayEnabled && id
      ? generateIntradayBatchUrl(accountAlias, id, currentDate)
      : null,
  }, "data")
}
